import { useState, useEffect, useCallback } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  ApplicantData,
  Conditions,
  ScoreBand,
  ScoreBreakdownItem,
  ScorecardEngine,
  ScoreResult,
} from '@trnsact/trnsact-scorecard-sdk';
import { FACT_NAMES_BOOLEAN, FACT_NAMES_NUMERIC, FACT_NAMES_STRING } from '@trnsact/business-criteria';
import _ from 'lodash';
import { SelectOption } from 'global';
import { ScorecardCriteria } from '../types';
import { CREATE_CREDIT_TIERS_RESULTS, GET_SCORECARD } from '../queries';
import { LenderWaterfallPrescreenCriteriaResultType, CreditApplication } from '@trnsact/trnsact-shared-types';
import { ConditionsFieldsTypes } from '../../../../modules/jsonRulesCriterias/scorecardCriteria/types';
import { determineRuleType } from '../../../../modules/jsonRulesCriterias/scorecardCriteria/lib';
import { buildApplicantData, transformApplicantForScoring, extractStringOptions } from '../utils/scorecardUtils';

export const useScorecard = (
  creditApplication: CreditApplication,
  equipmentData: any,
  vo: any,
  cs: any,
  account: any,
  portalConfigurationDataLoading: boolean
) => {
  const [createCreditTiersResults, { error }] = useMutation(CREATE_CREDIT_TIERS_RESULTS);
  const [scorecardCriteria, setScorecardCriteria] = useState<ScorecardCriteria | null>(null);
  const [financeProgramId, setFinanceProgramId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [totalScore, setTotalScore] = useState<number>(0);
  const [band, setBand] = useState<string>('');
  const [bandColor, setBandColor] = useState<string>('#FFF');
  const [breakDown, setBreakDown] = useState<ScoreBreakdownItem[]>([]);
  const [results, setResults] = useState<
    | (ScoreResult & {
        band?: string;
        bandColor?: string;
      })
    | null
  >(null);
  const [applicantData, setApplicantData] = useState<ApplicantData | null>(null);
  const [listOfFacts, setListOfFacts] = useState<
    Map<
      string,
      {
        fact: string;
        type: ConditionsFieldsTypes;
        factType: FACT_NAMES_BOOLEAN | FACT_NAMES_NUMERIC | FACT_NAMES_STRING;
      }
    >
  >(new Map());
  const [stringFactOptions, setStringFactOptions] = useState<Map<string, SelectOption[]>>(new Map());
  const [manualFields, setManualFields] = useState<{ [key: string]: any }>({});

  useQuery(GET_SCORECARD, {
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      setScorecardCriteria(data.scoreCard[0]?.prescreenCriteria);
      setFinanceProgramId(data.scoreCard[0]?.financeProgramId);
      setIsLoading(false);
    },
  });

  useEffect(() => {
    setIsLoading(!creditApplication || !vo);
  }, [creditApplication, vo, portalConfigurationDataLoading]);

  useEffect(() => {
    if (!isLoading && scorecardCriteria) {
      let applicant = buildApplicantData(creditApplication, equipmentData, vo, cs);

      // Convert manualFields values to their correct types
      const parsedManualFields: { [key: string]: any } = {};
      Object.entries(manualFields).forEach(([k, v]) => {
        if (typeof v === 'string') {
          if (v.trim() !== '' && !isNaN(Number(v))) {
            parsedManualFields[k] = Number(v);
          } else if (v === 'true' || v === 'false') {
            parsedManualFields[k] = v === 'true';
          } else {
            parsedManualFields[k] = v;
          }
        } else {
          parsedManualFields[k] = v;
        }
      });

      applicant = { ...applicant, ...parsedManualFields };

      const factsMap = new Map();

      scorecardCriteria.jsonCriteria.scoreCriteria.forEach((scoreCardRow: any) => {
        scoreCardRow?.jsonRules.conditions.all.forEach((cond: any) => {
          const factName = cond.fact;
          const fieldType = determineRuleType(factName);
          factsMap.set(factName, { fact: factName, type: fieldType, factType: factName });
        });

        if (scoreCardRow && scoreCardRow?.customCriteria) {
          Object.values(scoreCardRow?.customCriteria).forEach((criteria: any) => {
            let fieldType: ConditionsFieldsTypes;
            switch (criteria.type) {
              case 'boolean':
                fieldType = ConditionsFieldsTypes.Boolean;
                break;
              case 'number':
              case 'currency':
                fieldType = ConditionsFieldsTypes.Number;
                break;
              default:
                fieldType = ConditionsFieldsTypes.String;
            }
            factsMap.set(criteria.name, { fact: criteria.name, type: fieldType, factType: criteria.name });
          });
        }
      });

      setListOfFacts(factsMap);
      setStringFactOptions(extractStringOptions(scorecardCriteria));
      setApplicantData(applicant);
    }
  }, [isLoading, scorecardCriteria, manualFields, cs, creditApplication, equipmentData, vo]);

  useEffect(() => {
    if (results) {
      setTotalScore(results.totalScore || 0);
      setBandColor(results.bandColor || '#FFF');
      setBand(results.band || '');
      setBreakDown(results.breakdown);
    }
  }, [results]);

  const handleReRunScoreCard = useCallback(async () => {
    if (!scorecardCriteria || !applicantData) return;

    let multiCriteriaLayers: Array<{ conditions: { all: Array<any> } }> = [];
    scorecardCriteria.jsonCriteria.scoreCriteria.forEach(criterias => {
      try {
        let customCriteria: any = [];
        let presetCriteria: any = [];
        if (!_.isEmpty(criterias.customCriteria)) {
          customCriteria = Conditions.transformRuleObject(criterias.customCriteria).conditions.all;
        }
        if (!_.isEmpty(criterias.jsonRules)) {
          presetCriteria = criterias.jsonRules.conditions.all.map(condition => {
            let value = condition.value;
            if (typeof value === 'string') {
              if (value.trim() !== '' && !isNaN(Number(value))) {
                value = Number(value);
              } else if (value === 'true' || value === 'false') {
                value = value === 'true';
              }
            }

            return {
              ...condition,
              value,
            };
          });
        }
        multiCriteriaLayers.push({
          conditions: {
            all: [
              ...(Array.isArray(presetCriteria) ? presetCriteria : []),
              ...(Array.isArray(customCriteria) ? customCriteria : []),
            ],
          },
        });
      } catch (error) {
        console.error(error);
      }
    });

    const transformedApplicant = transformApplicantForScoring(applicantData, manualFields, listOfFacts);
    const bands = scorecardCriteria.jsonCriteria.scoreTiers as ScoreBand[];
    const engine = new ScorecardEngine(multiCriteriaLayers, undefined, bands);
    setResults(await engine?.score(transformedApplicant));
  }, [scorecardCriteria, applicantData, manualFields, listOfFacts]);

  const handleSaveScoreCard = async (refetchCreditSubmission?: any) => {
    if (results) {
      const input = [
        {
          accountId: account?.id,
          vendorOpportunityId: vo?.vendorOpportunityId,
          processInstanceId: vo?.processInstanceId,
          prescreenCriteriaId: scorecardCriteria?.prescreenCriteriaId,
          creditSubmissionId: cs?.creditSubmissionId,
          financeProgramId,
          tiersType: 'SCORECARD',
          runtimeInput: applicantData,
          runtimeOutput: results,
          result: LenderWaterfallPrescreenCriteriaResultType.PASS,
          highestTierPassed: results.band || '',
          highestTierColor: results.bandColor || '',
        },
      ];
      setIsLoading(true);
      await handleCreateResults(input);
      if (refetchCreditSubmission) {
        await refetchCreditSubmission();
      }
      setIsLoading(false);
    }
  };

  const handleCreateResults = async (inputArray: any) => {
    try {
      await createCreditTiersResults({ variables: { input: inputArray } });
    } catch (err) {
      console.log(error);
      debugger;
    }
  };

  useEffect(() => {
    if (applicantData && financeProgramId) {
      handleReRunScoreCard();
    }
  }, [applicantData, financeProgramId, manualFields, handleReRunScoreCard]);

  return {
    scorecardCriteria,
    isLoading,
    totalScore,
    band,
    bandColor,
    breakDown,
    applicantData,
    listOfFacts,
    stringFactOptions,
    manualFields,
    setManualFields,
    handleSaveScoreCard,
    handleReRunScoreCard,
  };
};
